/**
 * Phone number utilities for validation and formatting
 */

/**
 * HTML pattern attribute for phone number input fields.
 * Allows digits, parentheses, spaces, and hyphens.
 * 
 * NOTE: Parentheses and hyphen must be escaped for RegExp v-flag compatibility
 * (modern browsers use v-flag by default for pattern validation)
 */
export const PHONE_INPUT_PATTERN = '[0-9\\(\\)\\s\\-]*';

/**
 * RegExp version of the phone input pattern for JavaScript validation
 */
export const PHONE_INPUT_REGEX = /^[0-9()\s-]*$/;

/**
 * Validates if a string matches the phone input pattern.
 * This checks the format during typing, not if it's a complete valid phone number.
 * 
 * @param value - The input value to validate
 * @returns true if the value matches the allowed phone input characters
 */
export const isValidPhoneInput = (value: string): boolean => {
  if (!value) return true;
  return PHONE_INPUT_REGEX.test(value);
};

/**
 * Extracts only digits from a phone string
 * 
 * @param phone - The phone string to extract digits from
 * @returns String containing only digits
 */
export const extractPhoneDigits = (phone: string): string => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

/**
 * Checks if a phone number has the minimum required digits (10 for US)
 * 
 * @param phone - The phone string to check
 * @returns true if the phone has at least 10 digits
 */
export const hasMinimumPhoneDigits = (phone: string): boolean => {
  const digits = extractPhoneDigits(phone);
  return digits.length >= 10;
};
